import { faBook, faHandsHoldingCircle, faHouse, faLink, faUser, faBookOpenReader } from '@fortawesome/free-solid-svg-icons';

import { Action } from '@shared/models/action.enum';
import { Model } from '@shared/models/model.enum';
import { RowData } from '@shared/models/row-data.type';

import { Language } from '@/organisations/models/language.enum';
import { ProductType } from '@/organisations/models/product.model';

const MS_PER_S = 1000;
const SEC_PER_MIN = 60;
const SENTRY_REPLAY_LENGTH_IN_MINUTES = 10;

function getEndpoints(api: string) {
    return {
        bannerMessagesList: `${api}banners/`,
        currentUser: `${api}auth/user/`,
        documentTemplateList: `${api}template-documents/`,
        login: `${api}auth/login/`,
        loginWithCode: `${api}login/code/`,
        communicationToken: `${api}login/communication-token/`,
        urlTokenInfo: `${api}login/url-token-info/`,
        logout: `${api}auth/logout/`,
        passwordReset: `${api}auth/password/reset/`,
        passwordResetConfirm: `${api}auth/password/reset/confirm/`,
        register: `${api}auth/register/`,
        resendEmail: `${api}auth/register/resend-email/`,
        refreshToken: `${api}auth/token/refresh/`,
        verifyEmail: `${api}auth/register/verify-email/`,
        silverfinLogin: `${api}silverfin/login/`,
        connectionPlatformList: `${api}connections/platforms/`,
        rolePermissions: `${api}role-permissions/`,
        user: (id: number) => `${api}users/${id}/`,
        users: `${api}users/`,
        userInvitationsList: (id: number) => `${api}users/${id}/invitations/`,
        userInvitationDetail: (userId: number, invitationId: number) => `${api}users/${userId}/invitations/${invitationId}/`,
        userInvitationAccept: (userId: number, invitationId: number) => `${api}users/${userId}/invitations/${invitationId}/accept/`,
        userInvitationReject: (userId: number, invitationId: number) => `${api}users/${userId}/invitations/${invitationId}/reject/`,
        organisationDetails: (id: number) => `${api}organisations/${id}/`,
        organisationStatistics: (id: number) => `${api}organisations/${id}/statistics/`,
        organisationList: `${api}organisations/`,
        organisationConnectionDetails: (id: number, connectionId: number) => `${api}organisations/${id}/connections/${connectionId}/`,
        organisationConnectionList: (id: number) => `${api}organisations/${id}/connections/`,
        organisationConnectionCreationProcessList: (id: number) => `${api}organisations/${id}/connection-creation-processes/`,
        organisationConnectionCreationProcessDetail: (id: number, ccid: number) =>
            `${api}organisations/${id}/connection-creation-processes/${ccid}/`,
        organisationConnectionPersonalTaxSync: (id: number, connectionId: number) =>
            `${api}organisations/${id}/connections/${connectionId}/sync/`,
        organisationConnectionPersonalTaxSyncDetails: (id: number, connectionId: number, personalTaxSyncId: number) =>
            `${api}organisations/${id}/connections/${connectionId}/sync/${personalTaxSyncId}/`,
        organisationMemberDetails: (id: number, memberId: number) => `${api}organisations/${id}/members/${memberId}/`,
        organisationMemberList: (id: number) => `${api}organisations/${id}/members/`,
        organisationMailPreview: (id: number) => `${api}organisations/${id}/mail-preview/`,
        organisationNonProfitWaitingList: (id: number) => `${api}organisations/${id}/non-profit/waiting-list/`,
        organisationNonProfitWaitingListDetail: (oid: number, eid: number) => `${api}organisations/${oid}/non-profit/waiting-list/${eid}/`,
        organisationNonProfitExportFileMerges: (id: number) => `${api}organisations/${id}/non-profit/merges/`,
        organisationNonProfitExportFileMergesDetail: (oid: number, efmid: number) =>
            `${api}organisations/${oid}/non-profit/merges/${efmid}/`,
        organisationNonProfitExportFileMergesDetailStartMerge: (oid: number, efmid: number) =>
            `${api}organisations/${oid}/non-profit/merges/${efmid}/start-merge/`,
        organisationNonProfitExportFileMergesDetailCopyEntries: (oid: number, efmid: number) =>
            `${api}organisations/${oid}/non-profit/merges/${efmid}/copy-entries/`,
        organisationInvitationList: (id: number) => `${api}organisations/${id}/invitations/`,
        organisationInvitationDetails: (id: number, invitation: number) => `${api}organisations/${id}/invitations/${invitation}/`,
        organisationInvitationResend: (id: number, invitation: number) =>
            `${api}organisations/${id}/invitations/${invitation}/send_reminder/`,
        organisationPersonalTaxList: (id: number) => `${api}organisations/${id}/personal-tax/`,
        organisationPersonalTaxYears: (id: number) => `${api}organisations/${id}/personal-tax/years/`,
        organisationPersonalTaxDetails: (id: number, ptid: number) => `${api}organisations/${id}/personal-tax/${ptid}/`,
        organisationPersonalTaxHistory: (id: number, ptid: number) => `${api}organisations/${id}/personal-tax/${ptid}/history/`,
        organisationPersonalTaxExportList: (id: number, ptid: number) => `${api}organisations/${id}/personal-tax/${ptid}/exports/`,
        organisationPersonalTaxBulkExportList: (id: number) => `${api}organisations/${id}/personal-tax/bulk-exports/`,
        organisationPersonalTaxBulkExportDetails: (id: number, eId: number) =>
            `${api}organisations/${id}/personal-tax/bulk-exports/${eId}/`,
        organisationPersonalTaxBulkImportList: (id: number) => `${api}organisations/${id}/personal-tax/bulk-imports/`,
        organisationPersonalTaxBulkImportDetail: (id: number, iId: number) => `${api}organisations/${id}/personal-tax/bulk-imports/${iId}/`,
        organisationPersonalTaxCalculateList: (id: number, ptid: number) => `${api}organisations/${id}/personal-tax/${ptid}/calculate/`,
        organisationPersonalTaxOptimizationList: (id: number, ptid: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/optimizations/`,
        organisationPersonalTaxOptimizationDetail: (id: number, ptid: number, oid: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/optimizations/${oid}/`,
        organisationPersonalTaxCalculationList: (id: number, ptid: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/calculations/`,
        organisationPersonalTaxCalculationDetail: (id: number, ptid: number, cid: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/calculations/${cid}/`,
        organisationPersonalTaxLinkList: (id: number, ptid: number) => `${api}organisations/${id}/personal-tax/${ptid}/links/`,
        organisationPersonalTaxLinkSync: (id: number, ptid: number) => `${api}organisations/${id}/personal-tax/${ptid}/links/sync/`,
        organisationPersonalTaxLinkSyncDetails: (id: number, ptid: number, sid: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/links/sync/${sid}/`,
        organisationPersonalTaxLinkDetails: (id: number, ptid: number, lid: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/links/${lid}/`,
        organisationPersonalTaxImportList: (id: number) => `${api}organisations/${id}/personal-tax/imports/`,
        organisationPersonalTaxExportDetails: (id: number, ptid: number, eId: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/exports/${eId}/`,
        organisationPersonalTaxImportDetails: (id: number, ptid: number, iId: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/imports/${iId}/`,
        organisationPersonalTaxParsingsList: (id: number, ptid: number) => `${api}organisations/${id}/personal-tax/${ptid}/parsings/`,
        organisationPersonalTaxParsingDetails: (id: number, ptid: number, pid: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/parsings/${pid}/`,
        organisationPersonalTaxNotificationList: (id: number, ptid: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/notifications/`,
        organisationPersonalTaxNotificationDetails: (id: number, ptid: number, nid: number) =>
            `${api}organisations/${id}/personal-tax/${ptid}/notifications/${nid}/`,
        organisationRolesList: (id: number) => `${api}organisations/${id}/roles/`,
        organisationCompaniesList: (id: number) => `${api}organisations/${id}/companies/`,
        organisationCompaniesDetails: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/`,
        organisationCompanyTemplateFillActionsList: (id: number, cid: number) =>
            `${api}organisations/${id}/companies/${cid}/template-fill-actions/`,
        organisationCompanyTemplateFillActionsDetail: (id: number, cid: number, tid: number) =>
            `${api}organisations/${id}/companies/${cid}/template-fill-actions/${tid}/`,
        organisationCompaniesFollowersList: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/followers/`,
        organisationCompaniesUpdateFollowersList: (id: number, cid: number) =>
            `${api}organisations/${id}/companies/${cid}/update-followers/`,
        organisationCompanyWorkflowLookup: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/workflow-lookup/`,
        organisationCompanyPeriodLookup: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/period-lookup/`,
        organisationCompanySortLoop: (orgId: number, cid: number, pid: number, rid: number) =>
            `${api}organisations/${orgId}/companies/${cid}/periods/${pid}/reconciliations/${rid}/sortloop/`,
        organisationCompaniesAccounts: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/accounts/`,
        organisationCompaniesReconciliations: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/reconciliations/`,
        organisationCompaniesPeriods: (id: number, fid: number, cid: number) => `${api}organisations/${id}/companies/${cid}/periods?firm_id=${fid}`,
        organisationCompaniesDepreciationSyncsList: (id: number, cid: number) =>
            `${api}organisations/${id}/companies/${cid}/depreciation-syncs/`,
        organisationCompaniesDepreciationSyncsDetails: (id: number, cid: number, edsid: number) =>
            `${api}organisations/${id}/companies/${cid}/depreciation-syncs/${edsid}/`,
        organisationDepreciationSettings: (id: number) => `${api}organisations/${id}/depreciation-settings/`,
        // Customers & Suppliers import endpoints
        organisationCompaniesCustomersSuppliersSyncsList: (id: number, cid: number) =>
            `${api}organisations/${id}/companies/${cid}/customers-suppliers-syncs/`,
        organisationCompaniesCustomersSuppliersSyncsDetails: (id: number, cid: number, sid: number) =>
            `${api}organisations/${id}/companies/${cid}/customers-suppliers-syncs/${sid}/`,
        organisationCustomersSuppliersSettings: (id: number) => `${api}organisations/${id}/customers-suppliers-import-settings/`,
        organisationHistories: (id: number) => `${api}organisations/${id}/histories/`,
        organisationCompaniesDocImportsList: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/doc-imports/`,
        organisationCompaniesDocImportsDetails: (id: number, cid: number, did: number) =>
            `${api}organisations/${id}/companies/${cid}/doc-imports/${did}/`,
        organisationCompaniesAccessList: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/access/`,
        organisationCompaniesAccessDetails: (id: number, cid: number, aid: number) =>
            `${api}organisations/${id}/companies/${cid}/access/${aid}/`,
        organisationCompaniesVATImportsList: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/vat-imports/`,
        organisationCompaniesVATImportsDetails: (id: number, cid: number, vid: number) =>
            `${api}organisations/${id}/companies/${cid}/vat-imports/${vid}/`,
        organisationVATPerPeriod: (id: number, month: number, year: number) =>
            `${api}organisations/${id}/dashboard/vat-per-period/?month=${month}&year=${year}`,
        organisationDashboardVATStats: (id: number) =>
            `${api}organisations/${id}/dashboard/vat-import-stats/`,
        organisationDashboardDepreciationPerPeriod: (id: number, month: number, year: number) =>
            `${api}organisations/${id}/dashboard/depreciation-per-period/?month=${month}&year=${year}`,
        organisationDashboardDepreciationStats: (id: number) =>
            `${api}organisations/${id}/dashboard/depreciation-stats/`,
        organisationDashboardSuspenseAccounts: (id: number, year: number, month: number) =>
            `${api}organisations/${id}/dashboard/suspense-accounts/?month=${month}&year=${year}`,
        organisationDashboardCompanySync: (id: number) => `${api}organisations/${id}/dashboard/execute-sync/`,
        organisationDashboardGetCompanySync: (id: number, syncId: number) =>
            `${api}organisations/${id}/dashboard/sync/?sync_id=${syncId}`,
        organisationDashboardSuspenseAccountStats: (id: number) =>
            `${api}organisations/${id}/dashboard/suspense-accounts-stats/`,
        organisationDashboardGetAnalyses: (id: number, month: number, year: number) =>
            `${api}organisations/${id}/dashboard/get-data-analyses/?month=${month}&year=${year}`,
        organisationDashboardExecuteAnalysis: (id: number) => `${api}organisations/${id}/dashboard/execute-data-analysis/`,
        organisationDashboardGetSupplies: (id: number, month:number, year: number) =>
            `${api}organisations/${id}/dashboard/get-supply-accounts/?month=${month}&year=${year}`,
        organisationDashboardGetSupplyStats: (id: number) =>
            `${api}organisations/${id}/dashboard/get-supply-account-stats/`,
        organisationDashboardVATIC:(id:number, month:number, year: number) =>
            `${api}organisations/${id}/dashboard/vat-ic/?month=${month}&year=${year}`,
        organisationDashboardVATICStats:(id:number) =>
            `${api}organisations/${id}/dashboard/vat-ic-stats/`,
        organisationCompaniesBudgetList: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/budgets/`,
        organisationCompaniesBudgetImportsList: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/budget-imports/`,
        organisationCompaniesBudgetImportsDetails: (id: number, cid: number, vid: number) =>
            `${api}organisations/${id}/companies/${cid}/budget-imports/${vid}/`,
        organisationCompanyScenariosList: (id: number, cid: number) => `${api}organisations/${id}/companies/${cid}/scenarios/`,
        organisationGenericScenariosList: (id: number) => `${api}organisations/${id}/generic-scenarios/`,
        organisationCompanyScenariosDetails: (id: number, cid: number, sid: number) =>
            `${api}organisations/${id}/companies/${cid}/scenarios/${sid}/`,
        organisationCompaniesScenariosContent: (id: number, cid: number, sid: number) =>
            `${api}organisations/${id}/companies/${cid}/scenarios/${sid}/content/`,
        organisationCompanyScenariosScenarioRunsList: (id: number, cid: number, sid: number) =>
            `${api}organisations/${id}/companies/${cid}/scenarios/${sid}/runs/`,
        organisationCompanyScenariosScenarioRunsDetails: (id: number, cid: number, sid: number, rid: number) =>
            `${api}organisations/${id}/companies/${cid}/scenarios/${sid}/runs/${rid}/`,
        organisationKnowledgeCenterConversationList: (id: number) => `${api}organisations/${id}/knowledge-center/conversations/`,
        organisationKnowledgeCenterConversationDetails: (id: number, cid: number) =>
            `${api}organisations/${id}/knowledge-center/conversations/${cid}/`,
        organisationKnowledgeCenterConversationFileList: (id: number, cid: number) =>
            `${api}organisations/${id}/knowledge-center/conversations/${cid}/conversation-files/`,
        organisationKnowledgeCenterMessageList: (id: number, cid: number) =>
            `${api}organisations/${id}/knowledge-center/conversations/${cid}/messages/`,
        organisationCompanyKnowledgeCenterWebsocket: (id: number, cid: number) =>
            `${api.replace('https', 'wss')}organisations/${id}/conversations/${cid}/`,
        organisationKnowledgeCenterRagfileList: (id: number) => `${api}organisations/${id}/knowledge-center/files/`,
        organisationKnowledgeCenterRagfileDetail: (id: number, rid: number) => `${api}organisations/${id}/knowledge-center/files/${rid}/`,
        organisationKnowledgeCenterRagfileStatusList: (id: number, rid: number) =>
            `${api}organisations/${id}/knowledge-center/files/${rid}/runs/`,
        organisationKnowledgeCenterRagfileStatusDetails: (id: number, rid: number, sid: number) =>
            `${api}organisations/${id}/knowledge-center/files/${rid}/runs/${sid}`,
        organisationKnowledgeCenterListSharepointSites: (orgId: number) =>
            `${api}organisations/${orgId}/knowledge-center/sharepoint-sites/followed/`,
        organisationKnowledgeCenterSelectedSite: (orgId: number) => `${api}organisations/${orgId}/knowledge-center/sharepoint-sites/`,
        organisationKnowledgeCenterSharePointSiteDetails: (orgId: number, siteId: number) =>
            `${api}organisations/${orgId}/knowledge-center/sharepoint-sites/${siteId}/`,
        organisationKnowledgeCenterListPredefinedQuestions: (orgId: number) =>
            `${api}organisations/${orgId}/knowledge-center/predefinedquestions/`,
        organisationKnowledgeCenterListTokenCounters: (orgId: number) => `${api}organisations/${orgId}/knowledge-center/tokencounters/`,
        organisationKnowledgeCenterListAthenaUsageTiers: (orgId: number) => `${api}organisations/${orgId}/knowledge-center/usagetiers/`,
        organisationAnnualReportList: (orgId: number) => `${api}organisations/${orgId}/annual-reports/`,
        organisationAnnualReportDetails: (orgId: number, arId: number) => `${api}organisations/${orgId}/annual-reports/${arId}/`,
        organisationAnnualReportPromptList: (orgId: number) => `${api}organisations/${orgId}/annual-reports/prompts/`,
        organisationAnnualReportPromptDetails: (orgId: number, arId: number) => `${api}organisations/${orgId}/annual-reports/prompts/${arId}/`,
        organisationRoleDetails: (orgId: number, roleId: number) => `${api}organisations/${orgId}/roles/${roleId}/`,
        organisationSubscriptionList: (orgId: number) => `${api}organisations/${orgId}/subscriptions/`,
        organisationSubscriptionDetails: (orgId: number, sid: number) => `${api}organisations/${orgId}/subscriptions/${sid}/`,
        organisationDivisions: (orgId: number) => `${api}organisations/${orgId}/eol/divisions/`,
        organisationDivision: (orgId: number, divisionId: string) => `${api}organisations/${orgId}/eol/divisions/${divisionId}/`,
        organisationAdministrations: (orgId: number) => `${api}organisations/${orgId}/yuki/administrations/`,
        products: `${api}products`,
        silverfinApiFirm: `${api}silverfin-api/firm/`,
        silverfinApiFirmLookup: `${api}silverfin-api/firm-lookup/`,
        taxQuestions: (orgId: number) => `${api}organisations/${orgId}/tax-questions/`,
        taxSettings: (orgId: number) => `${api}organisations/${orgId}/personal-tax/settings/`,
        taxQuestionsList: (orgId: number, companyId: number) => `${api}organisations/${orgId}/companies/${companyId}/tax-questions/`,
        taxQuestionsDetails: (orgId: number, companyId: number, qid: number) =>
            `${api}organisations/${orgId}/companies/${companyId}/tax-questions/${qid}/`,
        taxQuestionnaire: (orgId: number, taxId: number) => `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/`,
        taxQuestionnaireTemplates: (orgId: number) => `${api}organisations/${orgId}/personal-tax/settings/questionnaire-templates/`,
        taxQuestionnaireTemplatesDetails: (orgId: number, templateId: number) =>
            `${api}organisations/${orgId}/personal-tax/settings/questionnaire-templates/${templateId}/`,
        taxQuestionnaireSend: (orgId: number, taxId: number) => `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/send/`,
        taxQuestionnaireQuestionsList: (orgId: number, taxId: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/questions/`,
        taxQuestionnaireQuestionsListChangeOrder: (orgId: number, taxId: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/questions/change_order/`,
        taxQuestionnaireQuestionsDetails: (orgId: number, taxId: number, qid: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/questions/${qid}/`,
        taxQuestionnaireResponsesList: (orgId: number, taxId: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/responses/`,
        taxQuestionnaireResponsesDetails: (orgId: number, taxId: number, rid: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/responses/${rid}/`,
        taxQuestionnaireResponseDocsList: (orgId: number, taxId: number, rid: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/responses/${rid}/documents/`,
        taxQuestionnaireResponseDocsDetails: (orgId: number, taxId: number, rid: number, did: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/responses/${rid}/documents/${did}/`,
        taxQuestionnaireSyncToSilverfinList: (orgId: number, taxId: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/sync-to-silverfin/`,
        taxQuestionnaireSyncFromSilverfinList: (orgId: number, taxId: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/sync-from-silverfin/`,
        taxQuestionnaireSyncToSilverfinDetail: (orgId: number, taxId: number, syncId: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/sync-to-silverfin/${syncId}/`,
        taxQuestionnaireSyncFromSilverfinDetail: (orgId: number, taxId: number, syncId: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/sync-from-silverfin/${syncId}/`,
        taxQuestionnaireSubmissionList: (orgId: number, taxId: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/submissions/`,
        taxQuestionnaireSubmissionDetails: (orgId: number, taxId: number, submId: number) =>
            `${api}organisations/${orgId}/personal-tax/${taxId}/questionnaire/submissions/${submId}/`,
        testsParsers: `${api}tests/parsers/`,
    };
}

export const environment = {
    production: false,

    version: '1.0.0',

    // The api endpoints
    api: `https://localhost:3000/`,

    // This is only here so typescript can find the endpoint types without us having to repeat them:
    endpoints: getEndpoints(''),

    supportedLocales: [Language.NL, Language.FR, Language.EN],
    defaultLocale: Language.NL,

    // Add extra configuration underneath this line

    mmfImportBlocked: {
        active: false,
        message: $localize`:@@mmf.blocked:De synchronisatie met MyMinfin/ToW is tijdelijk offline en zal terug beschikbaar zijn wanneer Tax-on-Web aanslagjaar 2024 wordt opengesteld. Bij eventuele vragen contacteer gerust onze support via support@fintrax.io`,
    },

    getTopApplications: () => {
        return [

            {
                title: $localize`:@@applictation.overview.title:Overzicht`,
                icon: faHouse,
                baseLink: 'overview',
                link: 'organisation',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: undefined,
                tabs: [
                    {
                        title: $localize`:@@application.overview.organisation:Organisatie`,
                        link: 'organisation',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.overview.details:Organisatiedetails`,
                        link: 'details',
                        permissionActions: [Action.Change],
                        permissionModel: Model.Organisation,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.overview.communication:Communicatie`,
                        link: 'communication',
                        permissionActions: [Action.Change],
                        permissionModel: Model.Organisation,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.overview.users:Medewerkers`,
                        link: 'users',
                        permissionActions: [Action.View],
                        permissionModel: Model.Member,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.overview.roles:Rollen`,
                        link: 'roles',
                        permissionActions: [Action.Change, Action.Delete, Action.Add],
                        permissionModel: Model.OrganisationRole,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.overview.companies:Dossiers`,
                        link: 'companies',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.subscriptions.title:Apps`,
                        link: 'subscriptions',
                        permissionActions: [Action.View],
                        permissionModel: Model.Subscription,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.usage.title:Gebruik`,
                        link: 'usage',
                        permissionActions: [Action.View],
                        permissionModel: Model.OrganisationHistory,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: ProductType.FinancialDashboard,
                iconUrl: 'assets/icons/dashboard_icon_white.png',
                baseLink: 'financial-dashboard',
                link: 'overview',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: ProductType.FinancialDashboard,
                experimental: false,
                tabs: [
                    {
                        title: $localize`:@@application.financial-dashboard.overviewTitle:Overzicht`,
                        link: 'overview',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: $localize`:@@application.connections.title:Verbindingen`,
                icon: faLink,
                baseLink: 'connections',
                link: 'all',
                organisationApp: true,
                permissionActions: [Action.View],
                permissionModel: Model.Connection,
                showWarning: false,
                productTag: undefined,
                tabs: [
                    {
                        title: $localize`:@@application.connections.all:Allemaal`,
                        link: 'all',
                        permissionActions: [Action.View],
                        permissionModel: Model.Connection,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: ProductType.PersonalTax,
                iconUrl: 'assets/icons/Personenbelasting.png',
                baseLink: 'personal-tax',
                link: 'declarations',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: undefined,
                tabs: [
                    {
                        title: $localize`:@@application.tax.declarations:Aangiftes`,
                        link: 'declarations',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.tax.bulkImport:Dossieraanmaak`,
                        link: 'bulk-imports',
                        permissionActions: [Action.View],
                        permissionModel: Model.BulkImport,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.tax.questionnaireContent:Vragenlijstmail`,
                        link: 'communication',
                        permissionActions: [Action.Change],
                        permissionModel: Model.Organisation,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.tax.settings:Instellingen`,
                        link: 'settings',
                        permissionActions: [Action.View],
                        permissionModel: Model.PersonalTaxSettings,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: ProductType.Scenarios,
                iconUrl: 'assets/icons/Scenario.png',
                baseLink: 'scenarios',
                link: 'general',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: ProductType.Scenarios,
                experimental: true,
                tabs: [
                    {
                        title: $localize`:@@application.general.menuTitle:Algemeen`,
                        link: 'general',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: ProductType.Depreciations,
                iconUrl: 'assets/icons/Afschrijvingstabellen.png',
                baseLink: 'depreciations',
                link: 'general',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: ProductType.Depreciations,
                experimental: false,
                tabs: [
                    {
                        title: $localize`:@@application.settings.menuTitle:Instellingen`,
                        link: 'general',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: ProductType.VatImport,
                iconUrl: 'assets/icons/BtwReconciliatie.png',
                baseLink: 'vat-import',
                link: 'general',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: ProductType.VatImport,
                experimental: true,
                tabs: [
                    {
                        title: $localize`:@@application.general.menuTitle:Algemeen`,
                        link: 'general',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: ProductType.DocImport,
                iconUrl: 'assets/icons/DocImport.png',
                baseLink: 'doc-import',
                link: 'general',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: ProductType.DocImport,
                experimental: true,
                tabs: [
                    {
                        title: $localize`:@@application.general.menuTitle:Algemeen`,
                        link: 'general',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: ProductType.NonProfit,
                icon: faHandsHoldingCircle,
                baseLink: 'non-profit',
                link: 'general',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: ProductType.NonProfit,
                experimental: false,
                tabs: [
                    {
                        title: $localize`:@@application.general.menuTitle:Algemeen`,
                        link: 'general',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: ProductType.KnowledgeCenter,
                icon: faBook,
                baseLink: 'athena',
                link: 'conversations',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: ProductType.KnowledgeCenter,
                experimental: false,
                tabs: [
                    {
                        title: $localize`:@@application.knowledgeCenter.conversationsTitle:Gesprekken`,
                        link: 'conversations',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.knowledgeCenter.ragfilesTitle:Bestanden`,
                        link: 'files',
                        permissionActions: [Action.View],
                        permissionModel: Model.RagFile,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.knowledgeCenter.settings:Instellingen`,
                        link: 'settings',
                        permissionActions: [Action.View],
                        permissionModel: Model.SharePointSite,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: ProductType.AnnualReport,
                iconUrl: 'assets/icons/Jaarverslag.png',
                baseLink: 'annual-report',
                link: 'overview',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: ProductType.AnnualReport,
                experimental: false,
                tabs: [
                    {
                        title: $localize`:@@application.annualReport.menuTitle:Verslagen`,
                        link: 'overview',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.annualReport.settings:Instellingen`,
                        link: 'settings',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
            {
                title: ProductType.CustomersSuppliersImport,
                iconUrl: 'assets/icons/customersSuppliersImport.png',
                baseLink: 'customers-suppliers-import',
                link: 'general',
                organisationApp: true,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: ProductType.CustomersSuppliersImport,
                experimental: true,
                tabs: [
                    {
                        title: $localize`:@@application.customersSuppliers.settings:Instellingen`,
                        link: 'general',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
        ];
    },

    getBottomApplications: () => {
        return [
            {
                title: '{{USER}}',
                icon: faUser,
                baseLink: 'user',
                link: 'profile',
                organisationApp: false,
                permissionActions: undefined,
                permissionModel: undefined,
                showWarning: false,
                productTag: undefined,
                tabs: [
                    {
                        title: $localize`:@@application.user.profile:Profiel`,
                        link: 'profile',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                    {
                        title: $localize`:@@application.user.invitations:Uitnodigingen`,
                        link: 'invitations',
                        permissionActions: undefined,
                        permissionModel: undefined,
                        hiddenWhenInactive: false,
                        matchChildren: false,
                    },
                ],
            },
        ];
    },
    homeUrl: `/dashboard`,

    defaultLoginPath: '/dashboard',

    silverfinPlatformName: 'Silverfin',

    storageKeys: {
        JWT_ACCESS_TOKEN: 'fintrax.jwt.access.token',
        JWT_ACCESS_EXPIRY: 'fintrax.jwt.access.expiration',
        JWT_REFRESH_TOKEN: 'fintrax.jwt.refresh.token',
        JWT_REFRESH_EXPIRY: 'fintrax.jwt.refresh.expiration',
        REDIRECT_URL: 'fintrax.redirect.url',
        LOCALE: 'fintrax.user-locale',
        ORGANISATION_HISTORY_PREFIX: 'fintrax.organisation-history',
    },

    defaultMailColor: '#2d529f',

    minPasswordLength: 16,
    passwordSpecialCharacters: ['@', '!', '$', '#', '%', '^', '&', '*', '+'],
    maxFileSize: 10450000, //(bytes, about 10MB, backend limit)
    maxImgHeight: 256, // Avatar height, in pixels

    // Monitoring settings
    enableSentry: true,
    tracesSampleRate: 0.6,
    replaysSessionSampleRate: 0.1,
    maxReplayDuration: SENTRY_REPLAY_LENGTH_IN_MINUTES * SEC_PER_MIN * MS_PER_S,
    sentryEnvironment: 'localdev',

    getSentryReplayUrl: (sessionId?: string) => {
        return sessionId !== undefined ? `https://fintraxdev.sentry.io/replays/${sessionId}/` : '';
    },

    silverfinCompanyLink: (firmId: number, companyId: number, periodId?: number) => {
        return `https://live.getsilverfin.com/f/${firmId}/${companyId}/ledgers/${periodId ?? ''}`;
    },

    silverfinCompanyLinkNoLedger: (firmId: number, companyId: number) => {
        return `https://live.getsilverfin.com/f/${firmId}/${companyId}}`;
    },

    getTableRowOptions: (totalLength: number) => {
        // eslint-disable-next-line no-magic-numbers
        let options = [10, 25, 50, 100, 250, 500, 1000, 2500].filter((val) => val <= totalLength) as RowData[];
        options.push({showAll: $localize`:@@all:Alles`});
        return options;
    },
};

// Inject the API endpoints for the environment's api host
export function injectEndpoints(env: any) {
    env.endpoints = getEndpoints(env.api);
}
